// src/components/CampaignWizard/StepReview.tsx
import React from "react";
import {
	Row,
	Col,
	Card,
	CardBody,
	CardHeader,
	Badge,
	ListGroup,
	ListGroupItem,
	TabContent,
	TabPane,
	Nav,
	NavItem,
	NavLink,
} from "reactstrap";
import { ChannelType } from "./StepChannels";

export interface StepReviewProps {
	basicInfo: { name: string; description: string; campaignType: string };
	channels: Record<ChannelType, boolean>;
	audience: { type: string; csvFile?: File | null; filterJson?: any };
	schedule: { immediate: boolean; scheduledTime: Date | null; localTime: boolean; recurrence?: any };
	fallbackRules: Array<{ fromChannel: string; onEvents: string[]; toChannel: string; delayHours: number }>;
	costEstimate: { emailCost: number; smsCost: number; inAppCost: number; totalCost: number };
	previewData: { emailHtml: string; smsText: string; inApp: { title: string; body: string } };
	onBack: () => void;
	onSubmit: () => void;
}

const CHANNEL_LABELS: Record<ChannelType, string> = {
	EMAIL: "Email",
	SMS: "SMS",
	IN_APP: "In-App",
};

const StepReview: React.FC<StepReviewProps> = ({
	basicInfo,
	channels,
	audience,
	schedule,
	fallbackRules,
	costEstimate,
	previewData,
	onBack,
	onSubmit,
}) => {
	const enabled = (Object.keys(channels) as ChannelType[]).filter((c) => channels[c]);
	const scheduleLabel = schedule.immediate
		? "Immediately"
		: schedule.scheduledTime
			? `${schedule.scheduledTime.toLocaleString()}${schedule.localTime ? " (Local)" : ""}`
			: "—";
	const [previewTab, setPreviewTab] = React.useState<ChannelType>(enabled[0] || "EMAIL");

	return (
		<>
			<h4 className="mb-5 text-primary fw-bold">Review Your Campaign</h4>

			<Row className="gx-4 gy-4">
				{/* Details */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Details</strong>
						</CardHeader>
						<CardBody className="pt-3">
							<ListGroup flush>
								<ListGroupItem className="px-0 py-1">
									<strong>Name:</strong> {basicInfo.name}
								</ListGroupItem>
								<ListGroupItem className="px-0 py-1">
									<strong>Type:</strong> {basicInfo.campaignType}
								</ListGroupItem>
								{basicInfo.description && (
									<ListGroupItem className="px-0 py-1 text-muted">
										{basicInfo.description}
									</ListGroupItem>
								)}
							</ListGroup>
						</CardBody>
					</Card>
				</Col>

				{/* Channels */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Channels</strong>
						</CardHeader>
						<CardBody className="pt-3">
							{enabled.length > 0 ? (
								enabled.map((ch) => (
									<Badge key={ch} color="primary" pill className="me-2 mb-2">
										{CHANNEL_LABELS[ch]}
									</Badge>
								))
							) : (
								<span className="text-muted">None selected</span>
							)}
						</CardBody>
					</Card>
				</Col>

				{/* Audience */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Audience</strong>
						</CardHeader>
						<CardBody className="pt-3">
							<div>
								<strong>
									{audience.type === "STATIC" ? "Static CSV" : "Dynamic Filter"}
								</strong>
							</div>
							{audience.type === "STATIC" && audience.csvFile && (
								<div className="mt-2 text-muted">{audience.csvFile.name}</div>
							)}
						</CardBody>
					</Card>
				</Col>

				{/* Schedule */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Schedule</strong>
						</CardHeader>
						<CardBody className="pt-3">{scheduleLabel}</CardBody>
					</Card>
				</Col>

				{/* Fallback Rules */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Fallback Rules</strong>
						</CardHeader>
						<CardBody className="pt-3">
							{fallbackRules.length > 0 ? (
								fallbackRules.map((r, i) => (
									<div key={i} className="mb-2">
										<strong>
											{CHANNEL_LABELS[r.fromChannel as ChannelType]} →{" "}
											{CHANNEL_LABELS[r.toChannel as ChannelType]}
										</strong>
										<div className="small text-muted">
											On {r.onEvents.join(", ") || "any"} after {r.delayHours}h
										</div>
									</div>
								))
							) : (
								<span className="text-muted">None defined</span>
							)}
						</CardBody>
					</Card>
				</Col>

				{/* Cost */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Estimated Cost</strong>
						</CardHeader>
						<CardBody className="pt-3">
							<div className="d-flex justify-content-between mb-1">
								<small>Email:</small>
								<small>${costEstimate.emailCost.toFixed(2)}</small>
							</div>
							<div className="d-flex justify-content-between mb-1">
								<small>SMS:</small>
								<small>${costEstimate.smsCost.toFixed(2)}</small>
							</div>
							<div className="d-flex justify-content-between mb-1">
								<small>In-App:</small>
								<small>${costEstimate.inAppCost.toFixed(2)}</small>
							</div>
							<hr className="my-2" />
							<div className="d-flex justify-content-between fw-bold">
								<span>Total:</span>
								<span>${costEstimate.totalCost.toFixed(2)}</span>
							</div>
						</CardBody>
					</Card>
				</Col>

				{/* Live Preview */}
				{enabled.length > 0 && (
					<Col md={12}>
						<Card className="border-0 shadow-sm">
							<CardHeader className="bg-light border-0 d-flex align-items-center">
								<strong className="me-3">Preview</strong>
								<Nav pills>
									{enabled.map((ch) => (
										<NavItem key={ch}>
											<NavLink
												className={previewTab === ch ? "active" : ""}
												onClick={() => setPreviewTab(ch)}
											>
												{CHANNEL_LABELS[ch]}
											</NavLink>
										</NavItem>
									))}
								</Nav>
							</CardHeader>

							<CardBody className="pt-3">
								<TabContent activeTab={previewTab}>
									{enabled.includes("EMAIL") && (
										<TabPane tabId="EMAIL">
											<div
												className="border rounded p-3"
												style={{ minHeight: 120 }}
												dangerouslySetInnerHTML={{ __html: previewData.emailHtml }}
											/>
										</TabPane>
									)}
									{enabled.includes("SMS") && (
										<TabPane tabId="SMS">
											<pre className="border rounded p-3" style={{ minHeight: 120 }}>
												{previewData.smsText}
											</pre>
										</TabPane>
									)}
									{enabled.includes("IN_APP") && (
										<TabPane tabId="IN_APP">
											<h6>{previewData.inApp.title}</h6>
											<p>{previewData.inApp.body}</p>
										</TabPane>
									)}
								</TabContent>
							</CardBody>
						</Card>
					</Col>
				)}
			</Row>
		</>
	);
};

export default StepReview;
